/**
 * aides-coproprietes.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "stubs" /coproprietes/aides/[slug].
 *
 * Aides financières mobilisables par les copropriétés (syndicat des
 * copropriétaires, syndic, conseil syndical) pour les travaux
 * d'efficacité énergétique des parties communes et de l'enveloppe
 * collective.
 */

import type { AideStub } from './aides-particuliers';

export const AIDES_COPROPRIETES: AideStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'mpr-copro',
    iconName: 'piggy-bank',
    eyebrow: "Aide de l'Anah pour les copropriétés,",
    title: 'MaPrimeRénov\' Copropriétés',
    shortLabel: 'MPR Copro',
    lead: "L'aide principale pour rénover les parties communes de votre immeuble. Versée directement au syndicat, elle couvre les projets qui améliorent vraiment la performance énergétique.",
    amount: 'Selon barème Anah',
    amountLabel: 'Versement au syndicat des copropriétaires',
    keyPoints: [
      {
        iconName: 'building',
        label: 'Versée au syndicat',
        description: "L'aide est versée au syndicat des copropriétaires sur le compte bancaire séparé, pas aux copropriétaires individuellement.",
      },
      {
        iconName: 'gauge',
        label: 'Gain énergétique ≥ 35 %',
        description: "Le projet doit viser une amélioration énergétique d'au moins 35 % (diagnostic avant/après travaux à l'appui).",
      },
      {
        iconName: 'users',
        label: 'Bonus copropriétés modestes',
        description: "Majoration possible quand la part de copropriétaires aux revenus modestes est élevée, et pour les copropriétés en difficulté.",
      },
      {
        iconName: 'file-signature',
        label: 'Vote en AG préalable',
        description: "La résolution travaux doit être votée en AG avant signature du devis. L'Anah exige un mandat clair au syndic pour le dépôt du dossier.",
      },
    ],
    conditions: [
      'Copropriété immatriculée au registre national des copropriétés',
      "Immeuble à usage principal d'habitation, achevé depuis plus de 15 ans",
      'Au moins 65 % des lots affectés à la résidence principale',
      'Projet de travaux votés en AG visant un gain énergétique ≥ 35 %',
      'Diagnostic énergétique préalable réalisé conformément au cahier des charges Anah',
      "Maîtrise d'œuvre et entreprises titulaires des qualifications requises",
    ],
    cumulableWith: ['Aides collectifs', 'Éco-PTZ collectif', 'Aides locales', 'Fonds travaux'],
    faq: [
      {
        question: 'Qui touche la prime, le syndicat ou les copropriétaires ?',
        answer:
          "La prime est versée directement au syndicat des copropriétaires sur le compte bancaire séparé. Elle vient en déduction de l'appel de fonds travaux. Aucun copropriétaire ne touche individuellement la prime : la quote-part de chacun est mécaniquement réduite.",
      },
      {
        question: 'Que veut dire « 35 % de gain énergétique » ?',
        answer:
          "Le programme de travaux doit permettre de réduire d'au moins 35 % les consommations conventionnelles d'énergie primaire de l'immeuble, mesurées avant et après par le diagnostic réglementaire. Selon le saut de classe énergétique atteint, des bonifications peuvent s'appliquer.",
      },
      {
        question: "Comment se cumule MPR Copro avec les aides et l'éco-PTZ ?",
        answer:
          "Les trois dispositifs sont conçus pour être cumulés : MaPrimeRénov' Copropriétés (subvention), aides collectifs (prime), éco-PTZ collectif (financement à taux zéro du reste à charge). Le diagnostic préalable établit le plan de financement consolidé présenté en AG.",
      },
    ],
    ctaLabel: 'Estimer ma MPR Copro',
    ctaHref: '/coproprietes/diagnostic-cee',
    secondaryCta: { label: 'Préparer mon dossier AG', href: '/coproprietes/process-ag' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'cee-collectifs',
    iconName: 'sparkles',
    eyebrow: "Certificats d'Économies d'Énergie, collectif",
    title: 'Primes CEE collectives',
    shortLabel: 'CEE collectifs',
    lead: "Des aides pour les travaux collectifs de votre copropriété : isolation, chauffage collectif. Avec des bonus possibles selon les opérations.",
    amount: 'Selon fiche & volume',
    amountLabel: 'Versement après travaux',
    keyPoints: [
      {
        iconName: 'sparkles',
        label: 'Fiches enveloppe et thermique',
        description: "Catalogue d'opérations standardisées du secteur bâtiment résidentiel, applicables aux parties communes et équipements collectifs.",
      },
      {
        iconName: 'flame',
        label: 'Bonus applicable',
        description: "Majorations « Bonus Chauffage » et « Bonus Isolation » applicables aux opérations éligibles.",
      },
      {
        iconName: 'file-signature',
        label: 'Demande avant le devis',
        description: "L'aide doit être demandée avant signature du devis par le syndic. Sinon dossier non éligible.",
      },
      {
        iconName: 'building',
        label: 'Versée au syndicat',
        description: "L'aide collective est encaissée par le syndicat des copropriétaires et vient diminuer l'appel de fonds travaux voté en AG.",
      },
    ],
    conditions: [
      'Copropriété immatriculée et représentée par un syndic professionnel ou bénévole',
      "Travaux ou équipements éligibles à une fiche d'opération applicable (enveloppe ou thermique collectif)",
      "Demande d'aide avant signature du devis",
      "Réalisation par un professionnel disposant des qualifications requises",
      'Conservation des justificatifs : devis, factures, attestation signée par le maître d\'ouvrage (syndicat)',
    ],
    cumulableWith: ['MaPrimeRénov\' Copro', 'Éco-PTZ collectif', 'Aides locales'],
    faq: [
      {
        question: 'Quelles fiches pour une copropriété ?',
        answer:
          "Les principales fiches collectives sont les fiches enveloppe (isolation murs, toitures, planchers, fenêtres) et les fiches thermique collectif (chaufferies, pompes à chaleur collectives, ventilation, régulation, calorifugeage). On regarde votre immeuble et on chiffre les fiches qui s'appliquent.",
      },
      {
        question: 'Le bonus s\'applique-t-il aux copropriétés ?',
        answer:
          "Oui, les dispositifs de bonus prévoient des majorations applicables aux opérations collectives éligibles, notamment pour le remplacement de chaudière collective fioul/gaz par une solution renouvelable et pour certaines opérations d'isolation.",
      },
      {
        question: 'Aides et MPR Copro sont-elles cumulables ?',
        answer:
          "Oui — c'est la combinaison standard pour les rénovations d'ampleur en copropriété : MaPrimeRénov' Copropriétés (Anah) + aides collectifs + éco-PTZ collectif sur le reste à charge + aides locales éventuelles. Le plan de financement consolidé est présenté en AG.",
      },
    ],
    ctaLabel: 'Estimer mes primes CEE',
    ctaHref: '/coproprietes/diagnostic-cee',
    secondaryCta: { label: 'Voir les fiches BAR', href: '/cee/fiches?secteur=BAR' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'eco-ptz-collectif',
    iconName: 'banknote',
    eyebrow: 'Prêt à taux zéro pour les copropriétés,',
    title: 'Éco-PTZ collectif',
    shortLabel: 'Éco-PTZ collectif',
    lead: "Un prêt à taux zéro pour le syndicat, sans intérêts. Pensé pour les rénovations d'ampleur des parties communes.",
    amount: "Jusqu'à 50 000 € / lot",
    amountLabel: 'Prêt sans intérêt — travaux d\'ampleur',
    keyPoints: [
      {
        iconName: 'banknote',
        label: 'Taux zéro réel',
        description: "Le syndicat ne rembourse que le capital — aucun intérêt. Les frais bancaires restent à charge selon le contrat.",
      },
      {
        iconName: 'building',
        label: 'Souscrit par le syndicat',
        description: "L'éco-PTZ collectif est contracté par le syndicat des copropriétaires, après vote en AG, auprès d'une banque conventionnée par l'État.",
      },
      {
        iconName: 'users',
        label: 'Quote-part individuelle',
        description: "Chaque copropriétaire rembourse sa quote-part du prêt via les appels de fonds, au prorata des tantièmes votés en AG.",
      },
      {
        iconName: 'gauge',
        label: 'Parcours « travaux d\'ampleur »',
        description: "Plafond renforcé pour les rénovations globales atteignant un gain énergétique ≥ 35 %. Plafonds inférieurs pour un geste isolé.",
      },
    ],
    conditions: [
      "Travaux d'amélioration énergétique sur les parties communes (ou portant sur des équipements communs)",
      "Vote du recours à l'éco-PTZ collectif en AG, avec mandat au syndic pour la souscription",
      "Banque distributrice conventionnée avec l'État pour l'éco-PTZ",
      "Pour les copropriétaires souhaitant être exonérés du remboursement collectif : possibilité d'un éco-PTZ individuel complémentaire",
      'Compatibilité avec le plan pluriannuel de travaux (PPT) et le diagnostic technique global (DTG) éventuel',
    ],
    cumulableWith: ['MaPrimeRénov\' Copro', 'Aides collectifs', 'Aides locales'],
    faq: [
      {
        question: 'Tous les copropriétaires sont-ils obligés de rembourser ?',
        answer:
          "Le principe est un remboursement collectif via les appels de fonds, au prorata des tantièmes. Un copropriétaire peut toutefois, dans certains cas, demander à régler sa quote-part comptant pour ne pas participer au remboursement du prêt collectif. Les modalités exactes sont précisées dans la résolution AG et le contrat bancaire.",
      },
      {
        question: "L'éco-PTZ collectif est-il cumulable avec MPR Copro ?",
        answer:
          "Oui. L'éco-PTZ collectif finance typiquement le reste à charge après application des subventions (MPR Copro + aides collectifs + aides locales). C'est la brique de financement qui permet d'étaler la quote-part travaux sur la durée du prêt sans coût pour les copropriétaires.",
      },
      {
        question: 'Quelle est la durée de remboursement ?',
        answer:
          "La durée maximale de remboursement est encadrée par la réglementation éco-PTZ (jusqu'à 20 ans selon le type de travaux). La durée précise dépend du programme de travaux financé et de l'offre de la banque conventionnée retenue.",
      },
    ],
    ctaLabel: 'Étudier mon plan de financement',
    ctaHref: '/coproprietes/diagnostic-cee',
    secondaryCta: { label: 'Préparer le vote en AG', href: '/coproprietes/process-ag' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'aides-locales',
    iconName: 'landmark',
    eyebrow: 'Régions, métropoles, Anah OPAH',
    title: 'Aides locales copropriétés',
    shortLabel: 'Aides locales',
    lead: "En plus des aides nationales, certaines régions, métropoles et dispositifs locaux proposent des aides ciblées pour les copropriétés. Variables selon votre territoire.",
    amount: 'Variable',
    amountLabel: 'Selon territoire et dispositif',
    keyPoints: [
      {
        iconName: 'landmark',
        label: 'Régions et métropoles',
        description: "Conseil régional, métropole, EPCI : aides directes à la rénovation des copropriétés, parfois conditionnées au passage par un opérateur conventionné.",
      },
      {
        iconName: 'building',
        label: 'OPAH copropriétés',
        description: "Opérations Programmées d'Amélioration de l'Habitat pilotées par l'Anah et la collectivité, sur un périmètre géographique défini.",
      },
      {
        iconName: 'shield-check',
        label: 'Copropriétés en difficulté',
        description: "Régimes spécifiques (Plan de Sauvegarde) avec aides renforcées pour les copropriétés fragiles ou dégradées.",
      },
      {
        iconName: 'handshake',
        label: 'Accompagnement obligatoire',
        description: "Certaines aides locales conditionnent leur attribution au passage par une assistance à maîtrise d'ouvrage conventionnée.",
      },
    ],
    conditions: [
      "Localisation de l'immeuble dans la zone géographique éligible (région, métropole, EPCI, périmètre OPAH)",
      "Projet de travaux correspondant aux orientations du dispositif local (priorités énergétiques, fragilité, patrimoniale)",
      "Dépôt du dossier dans les fenêtres d'appel à projets ou en file de l'opération programmée",
      'Cumul avec MPR Copro et aides collectifs soumis aux plafonds d\'aides publiques',
      'Vote en AG du recours au dispositif et du mandat au syndic pour le dépôt du dossier',
    ],
    cumulableWith: ['MaPrimeRénov\' Copro', 'Aides collectifs', 'Éco-PTZ collectif'],
    faq: [
      {
        question: 'Comment savoir si ma copropriété est en OPAH ?',
        answer:
          "Renseignez-vous auprès du service habitat de votre commune, de votre métropole ou de l'ADIL. L'Anah recense également les opérations programmées en cours sur son site. Votre immeuble doit être inclus dans le secteur opérationnel.",
      },
      {
        question: "Une copropriété en difficulté peut-elle prétendre à des aides renforcées ?",
        answer:
          "Oui. Les copropriétés en difficulté ou dégradées peuvent bénéficier de régimes spécifiques (Plan de Sauvegarde, dispositifs Anah copropriétés fragiles) avec des taux d'aide renforcés et un accompagnement dédié. Le diagnostic préalable identifie l'éligibilité.",
      },
      {
        question: 'Que finance typiquement une région ou une métropole ?',
        answer:
          "Le périmètre varie selon les collectivités : aide au diagnostic énergétique, prime au saut de classe énergétique, bonification éco-PTZ, aide au financement de l'assistance à maîtrise d'ouvrage, soutien spécifique à certaines opérations (isolation extérieure, raccordement réseau de chaleur). Notre équipe vérifie les dispositifs ouverts sur votre territoire.",
      },
    ],
    ctaLabel: 'Identifier mes aides locales',
    ctaHref: '/contact?source=coproprietes&besoin=aides-locales',
    secondaryCta: { label: 'Voir MPR Copro', href: '/coproprietes/aides/mpr-copro' },
  },
];

export function getAideCopropriete(slug: string): AideStub | undefined {
  return AIDES_COPROPRIETES.find((a) => a.slug === slug);
}
